import { Badge, Group } from '@mantine/core';
import { useIntl } from 'react-intl';

import { shipmentDetailsSectionMessages } from './shipment-details-section-messages';
import styles from './ShipmentDetailsSection.module.css';
import { freightFormMessages } from '../../../freight-browser/components/freight-form/freight-form-messages';
import { DetailSection } from '../detail-section/DetailSection';

import type { LtlAddress, LtlAdditionalServices, LtlShipment } from '../../../../types/ltl';
import type { MessageDescriptor } from 'react-intl';

type ShipmentDetailsSectionProps = {
  shipment: LtlShipment;
  originAddress: LtlAddress | null;
  onCopy?: (value: string) => void;
};

type ServiceField = {
  key: keyof LtlAdditionalServices;
  label: MessageDescriptor;
};

// Mirrors the pickup accessorial key→message mapping in
// freight-browser/.../AccessorialsSection.tsx (lines 20-30); reuses those messages.
const PICKUP_SERVICE_FIELDS: ServiceField[] = [
  { key: 'isInsidePickup', label: freightFormMessages.accessorialInsidePickup },
  { key: 'isConstructionSitePickup', label: freightFormMessages.accessorialConstructionSitePickup },
  { key: 'isLiftgatePickup', label: freightFormMessages.accessorialLiftgatePickup },
  { key: 'isNotifyBeforePickup', label: freightFormMessages.accessorialNotifyBeforePickup },
  { key: 'isResidentialPickup', label: freightFormMessages.accessorialResidentialPickup },
  { key: 'isCarrierTerminalPickup', label: freightFormMessages.accessorialDropShipmentAtTerminal },
  { key: 'isFlatbedPickup', label: freightFormMessages.accessorialGroceryConsolidationPickup },
  { key: 'isLimitedAccessPickup', label: freightFormMessages.accessorialLimitedAccess },
  { key: 'isTradeShowPickup', label: freightFormMessages.accessorialTradeshowPickup },
];

// Mirrors the delivery accessorial key→message mapping (lines 32-43).
const DELIVERY_SERVICE_FIELDS: ServiceField[] = [
  { key: 'isInsideDelivery', label: freightFormMessages.accessorialInsideDelivery },
  { key: 'isConstructionSiteDelivery', label: freightFormMessages.accessorialConstructionSiteDelivery },
  { key: 'isLiftgateDelivery', label: freightFormMessages.accessorialLiftgateDelivery },
  { key: 'isNotifyBeforeDelivery', label: freightFormMessages.accessorialNotifyBeforeDelivery },
  { key: 'isResidentialDelivery', label: freightFormMessages.accessorialResidentialDelivery },
  { key: 'isHoldAtTerminal', label: freightFormMessages.accessorialHoldShipmentAtTerminal },
  { key: 'isFlatbedDelivery', label: freightFormMessages.accessorialGroceryConsolidationDelivery },
  { key: 'isAppointmentDelivery', label: freightFormMessages.accessorialAppointmentDelivery },
  { key: 'isLimitedAccessDelivery', label: freightFormMessages.accessorialLimitedAccess },
  { key: 'isTradeShowDelivery', label: freightFormMessages.accessorialTradeshowDelivery },
];

type AddressColumnProps = {
  title: string;
  address: LtlAddress | null;
  emptyLabel: string;
};

const AddressColumn = ({ title, address, emptyLabel }: AddressColumnProps) => {
  const cityStateLine = address ? [address.city, address.state, address.postalCode].filter(Boolean).join(', ') : '';

  return (
    <div className={styles.column}>
      <span className={styles.columnTitle}>{title}</span>
      {address === null ? (
        <span className={styles.empty}>{emptyLabel}</span>
      ) : (
        <>
          {address.name !== '' && <span className={styles.addressLine}>{address.name}</span>}
          {address.company !== '' && <span className={styles.addressLine}>{address.company}</span>}
          {address.line1 !== '' && <span className={styles.addressLine}>{address.line1}</span>}
          {address.line2 !== '' && <span className={styles.addressLine}>{address.line2}</span>}
          {cityStateLine !== '' && <span className={styles.addressLine}>{cityStateLine}</span>}
          {address.countryCode !== '' && <span className={styles.addressLine}>{address.countryCode}</span>}
          {address.phone !== '' && <span className={styles.addressLine}>{address.phone}</span>}
          {address.email !== '' && <span className={styles.addressLine}>{address.email}</span>}
        </>
      )}
    </div>
  );
};

type ServiceTagsProps = {
  title: string;
  fields: ServiceField[];
  services: LtlAdditionalServices;
};

const ServiceTags = ({ title, fields, services }: ServiceTagsProps) => {
  const intl = useIntl();
  const enabledFields = fields.filter((field) => services[field.key]);

  if (enabledFields.length === 0) return null;

  return (
    <div className={styles.services}>
      <span className={styles.servicesTitle}>{title}</span>
      <Group gap='xs'>
        {enabledFields.map((field) => (
          <Badge color='neutral' key={field.key}>
            {intl.formatMessage(field.label)}
          </Badge>
        ))}
      </Group>
    </div>
  );
};

/**
 * Left-column Shipment Details card: Ship From / Ship To addresses plus
 * pickup/delivery accessorial service tags. Store-agnostic — all data arrives
 * via props. Address columns gracefully render an empty state when `null`.
 */
export const ShipmentDetailsSection = ({ shipment, originAddress }: ShipmentDetailsSectionProps) => {
  const intl = useIntl();
  const { additionalServices } = shipment;

  return (
    <DetailSection testId='shipment-details-section' title={intl.formatMessage(shipmentDetailsSectionMessages.sectionTitle)}>
      <div className={styles.columns}>
        <AddressColumn
          address={originAddress}
          emptyLabel={intl.formatMessage(shipmentDetailsSectionMessages.emptyAddress)}
          title={intl.formatMessage(shipmentDetailsSectionMessages.shipFromTitle)}
        />
        <AddressColumn
          address={shipment.destinationAddress}
          emptyLabel={intl.formatMessage(shipmentDetailsSectionMessages.emptyAddress)}
          title={intl.formatMessage(shipmentDetailsSectionMessages.shipToTitle)}
        />
      </div>

      {additionalServices !== null && (
        <>
          <ServiceTags
            fields={PICKUP_SERVICE_FIELDS}
            services={additionalServices}
            title={intl.formatMessage(shipmentDetailsSectionMessages.pickupServicesTitle)}
          />
          <ServiceTags
            fields={DELIVERY_SERVICE_FIELDS}
            services={additionalServices}
            title={intl.formatMessage(shipmentDetailsSectionMessages.deliveryServicesTitle)}
          />
        </>
      )}

      {/* §7.4 data gap: Pickup Window — not on LtlShipment yet, so never rendered. */}
      {/* §7.4 data gap: Pickup number (with a faCopy button → props.onCopy) — not on LtlShipment yet. */}
      {/* §7.4 data gap: Pickup Instructions — not on LtlShipment yet. */}
      {/* §7.4 data gap: Delivery Instructions — not on LtlShipment yet. */}
    </DetailSection>
  );
};
